import { Button, Grid, Typography } from '@mui/material';
import { eventService, IApiGetEventListParams, userService } from '@tap/data-providers';
import { IComponentCss, useFetchData } from '@tap/mui-ui';
import { ReactElement } from 'react';
import { ChangeHistoryEvent, ChangeHistoryEventSkeleton, ChangeHistoryEventSlotProps } from './ChangeHistoryEvent';
import { ChangeHistorySlotComponent } from './change-history.types';

/*
*   История изменений
*/

type ChangeHistoryListProps = {
    apiParams: Partial<IApiGetEventListParams>,
    slots?: ChangeHistorySlotComponent<ChangeHistoryEventSlotProps>,
    slotProps?: ChangeHistoryEventSlotProps,
}


const css: IComponentCss = {
    changeHistoryList: {
        mt: -1,
    },
    titlesWrap: {
        paddingLeft: '16px',
        pb: 2,
        borderBottom: ' 1px solid #e0e0e0',
    },
    titleWrap: {
        paddingRight: '20px',
    },
    noChanges: {
        mt: 2,
    },
    actionButton: {
        mt: 2,
        backgroundColor: '#F5F5F5',
        color: '#212121',
        marginRight: '10px',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#EBEBEB !important',
        },
    },
};


{/* Шаблон заголовка изменения */ }
const Title = ({ text }: { text: string }): ReactElement => {
    return (
        <Grid item
            md={4}
            xs={12}
            sx={css.titleWrap}>
            <Typography variant='b1'>{text}</Typography>
        </Grid >
    );
}

{/* Список история изменений */ }
export const ChangeHistoryList = ({
    apiParams,
    slots,
    slotProps,
}: ChangeHistoryListProps) => {
    const { schemaName, entity } = apiParams;

    const [columnListState] = useFetchData(() => eventService.getColumnList({ schemaName, entity }), []);

    const [eventListState] = useFetchData(async (props?: Partial<IApiGetEventListParams>) => {
        const { rows, hasNextPage } = await eventService.getEventList({
            ...apiParams,
            ...props,
        });
        const autorIds = rows.map(r => r.authorId).filter(r => !!r);
        const users = autorIds.length ? await userService.getWorkerList({ workerIds: [...new Set(autorIds)] }) : [];


        return {
            hasNextPage,
            rows: rows.map(eventItem => ({
                eventItem,
                author: users.find(u => u.id === eventItem.authorId),
            })),
        };
    }, [apiParams], {
        mergePages: (data, lastPage) => {
            const { hasNextPage } = lastPage;

            return {
                hasNextPage,
                rows: [
                    ...data?.rows ?? [],
                    ...lastPage.rows,
                ],
            };
        },
    });

    const handleLoadNextPage = () => {
        eventListState.fetchData({
            updateIsLoading: true,
            requestProps: { fromId: eventListState.data?.rows.at(-1)?.eventItem.id },
        });
    }

    const isDataLoading = columnListState.isLoading || eventListState.isLoading;

    return (
        <Grid sx={css.changeHistoryList}>

            {/* Заголовки*/}
            <Grid container sx={css.titlesWrap}>
                <Title text={'Поле'} />
                <Title text={'Предыдущее значение'} />
                <Title text={'Новое значение'} />
            </Grid>

            {!isDataLoading && !eventListState.data?.rows.length && (
                <Typography sx={css.noChanges}>Нет изменений</Typography>
            )}

            {(eventListState.data?.rows ?? []).map((row) => (
                <ChangeHistoryEvent
                    key={row.eventItem.id}
                    eventItem={row.eventItem}
                    author={row.author}
                    columnList={columnListState.data ?? []}
                    slots={slots}
                    slotProps={slotProps}
                />
            ))}

            {isDataLoading && <ChangeHistoryEventSkeleton />}

            {!isDataLoading && eventListState.data?.hasNextPage && (
                <Button
                    color='primary'
                    variant='contained'
                    disableElevation
                    sx={css.actionButton}
                    onClick={handleLoadNextPage}
                >
                    Предыдущие события
                </Button>
            )}


        </Grid>
    );
}
